import { Box, IconButton, Stack, TextField, Typography } from "@mui/material"
import { useEffect, useRef, useState } from "react"
import { useTheme } from "@mui/material/styles"
import Iconify from "minimals-template/components/Iconify"
import { If } from "lib/@components/switch"

export function ShareLinkCommentComposer({
    buttonLabel = "Comment",
    dataCy,
    label = "Comment",
    onSubmit,
    onFocus,
    contextLabel,
    contextText,
    onClearContext,
    autoFocus = false,
    publishing = false,
    minRows = 2,
}) {
    const theme = useTheme()
    const inputRef = useRef(null)
    const wasPublishingRef = useRef(false)
    const [body, setBody] = useState("")
    const [submitting, setSubmitting] = useState(false)
    const isLocked = submitting || publishing
    const sendDisabled = isLocked || !body.trim()

    useEffect(scheduleInputFocus, [autoFocus, contextLabel, isLocked])
    useEffect(clearBodyAfterPublish, [publishing])

    return (
        <Stack spacing={0.5} data-cy={dataCy} data-publishing={publishing ? "true" : undefined}>
            <If value={!!contextLabel}>
                <Box
                    data-cy={dataCy ? `${dataCy}-context` : undefined}
                    sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 0.5,
                        alignSelf: "flex-start",
                        px: 1,
                        py: 0.25,
                        borderRadius: theme.shape.borderRadius,
                        bgcolor: theme.palette.action.hover,
                        color: theme.palette.text.secondary,
                    }}
                >
                    <Iconify icon="mdi:map-marker-outline" />
                    <Typography variant="caption" noWrap>
                        Commenting on {contextLabel}
                    </Typography>
                    <IconButton
                        size="small"
                        onClick={onClearContext}
                        disabled={isLocked}
                        aria-label="Clear context"
                        data-cy={dataCy ? `${dataCy}-clear` : undefined}
                    >
                        <Iconify icon="mdi:close" />
                    </IconButton>
                </Box>
                <If value={!!contextText}>
                    <Typography
                        variant="caption"
                        color="text.secondary"
                        noWrap
                        data-cy={dataCy ? `${dataCy}-context-quote` : undefined}
                        sx={getContextQuoteSx()}
                    >
                        “{contextText}”
                    </Typography>
                </If>
            </If>
            <Stack direction="row" spacing={0.5} alignItems="flex-end">
                <TextField
                    fullWidth
                    multiline
                    minRows={minRows}
                    size="small"
                    autoFocus={autoFocus}
                    disabled={isLocked}
                    inputRef={inputRef}
                    placeholder={label}
                    value={body}
                    onChange={handleChange}
                    onFocus={handleFocus}
                    onKeyDown={handleKeyDown}
                    inputProps={{
                        "aria-label": label,
                        "data-cy": dataCy ? `${dataCy}-input` : undefined,
                    }}
                />
                <IconButton
                    color="primary"
                    size="small"
                    disabled={sendDisabled}
                    onClick={submit}
                    aria-label={buttonLabel}
                >
                    <Iconify icon={getSendIcon()} />
                </IconButton>
            </Stack>
        </Stack>
    )

    // Mirrors the anchored-quote excerpt on saved threads (share-link-comment-anchor-header) so the
    // text being commented on reads the same before and after publishing, and ties the composer to
    // the in-document draft highlight.
    function getContextQuoteSx() {
        return {
            display: "block",
            maxWidth: 1,
            borderLeft: `3px solid ${theme.palette.primary.main}`,
            borderRadius: 0,
            bgcolor: "background.neutral",
            px: 1,
            py: 0.5,
        }
    }

    function scheduleInputFocus() {
        if (isLocked || (!autoFocus && !contextLabel)) return undefined
        const timeout = window.setTimeout(focusTextarea, 0)
        return clearScheduledFocus

        function focusTextarea() {
            inputRef.current?.focus?.()
        }

        function clearScheduledFocus() {
            window.clearTimeout(timeout)
        }
    }

    function clearBodyAfterPublish() {
        if (wasPublishingRef.current && !publishing) {
            setBody("")
        }
        wasPublishingRef.current = publishing
    }

    // The plane goes solid the moment there is something to send.
    function getSendIcon() {
        return sendDisabled ? "lets-icons:send-light" : "lets-icons:send-fill"
    }

    function handleChange(event) {
        if (isLocked) return
        setBody(event.target.value)
    }

    // Focusing the field signals intent to write — the surrounding thread uses this to reveal its
    // context (select the comment, scroll its anchor into view) before the user types anything.
    function handleFocus() {
        onFocus?.()
    }

    function handleKeyDown(event) {
        if (isLocked || event.key !== "Enter" || event.shiftKey) return
        event.preventDefault()
        submit()
    }

    async function submit() {
        if (isLocked) return
        const trimmed = body.trim()
        if (!trimmed) return
        setSubmitting(true)
        try {
            await onSubmit(trimmed)
        } finally {
            setSubmitting(false)
        }
    }
}
